import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  Download,
  FileSpreadsheet,
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  XCircle,
  RefreshCw,
  Clock,
  TrendingUp,
  Users,
  Database,
  FileCheck,
  Zap,
  Cloud,
  Shield,
  Plus,
  FileText,
  Search,
  Grid,
  List
} from 'lucide-react';
import Button from '../../../../components/common/Button';
import Card from '../../../../components/ui/Card';
import Input from '../../../../components/common/Input';

const BulkUpload = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, processing, completed
  const [selectedTemplate, setSelectedTemplate] = useState('standard');
  const [searchQuery, setSearchQuery] = useState('');

  const templates = [
    { id: 'basic', name: 'Basic Template', description: 'Essential fields only', fields: 8, color: 'blue' },
    { id: 'standard', name: 'Standard Template', description: 'Recommended for most products', fields: 15, color: 'green' },
    { id: 'advanced', name: 'Advanced Template', description: 'Full specifications & variants', fields: 25, color: 'purple' },
    { id: 'custom', name: 'Custom Template', description: 'Your saved configuration', fields: 12, color: 'orange' }
  ];

  const stats = [
    { label: 'Total Uploads', value: '1,247', change: '+12%', icon: TrendingUp, color: 'green' },
    { label: 'Success Rate', value: '98.5%', change: '+2.3%', icon: CheckCircle, color: 'blue' },
    { label: 'Avg Processing', value: '2.4s', change: '-0.8s', icon: Clock, color: 'purple' },
    { label: 'Active Users', value: '156', change: '+24', icon: Users, color: 'orange' }
  ];

  // Format INR currency
  const formatINR = (amount) => {
    if (!amount) return '₹0.00';
    return `₹${parseFloat(amount).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      simulateUpload();
    }
  };

  const simulateUpload = () => {
    setUploadStatus('uploading');
    setUploadProgress(0);
    
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setUploadStatus('processing');
          setTimeout(() => {
            setUploadStatus('completed');
          }, 1500);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const resetUpload = () => {
    setFile(null);
    setUploadProgress(0);
    setUploadStatus('idle');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'uploading': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'processing': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'completed': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'uploading': return <RefreshCw className="h-5 w-5 animate-spin flex-shrink-0" />;
      case 'processing': return <Database className="h-5 w-5 flex-shrink-0" />;
      case 'completed': return <CheckCircle className="h-5 w-5 flex-shrink-0" />;
      default: return <Cloud className="h-5 w-5 flex-shrink-0" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="small"
            onClick={() => navigate('/seller/sales')}
            className="hover:bg-gray-100 flex items-center justify-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2 flex-shrink-0" />
            Back to Sales
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">Bulk Product Upload</h1>
            <p className="text-gray-600">Upload, validate, and process multiple products simultaneously</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            size="small"
            onClick={() => navigate('/seller/sales/add-product')}
            className="flex items-center justify-center"
          >
            <Plus className="h-4 w-4 mr-2 flex-shrink-0" />
            Add Single Product
          </Button>
          <Button
            variant="outline"
            size="small"
            onClick={() => navigate('/seller/sales/product-templates')}
            className="flex items-center justify-center"
          >
            <FileText className="h-4 w-4 mr-2 flex-shrink-0" />
            Use Templates
          </Button>
          <Button
            size="small"
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 flex items-center justify-center"
            onClick={() => document.getElementById('file-upload').click()}
          >
            <Zap className="h-4 w-4 mr-2 flex-shrink-0" />
            Quick Upload
          </Button>
        </div>
      </div>

      {/* Quick Search Bar */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
        <Input
          placeholder="Search recent uploads or templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          leftIcon={<Search className="h-5 w-5 text-gray-400 flex-shrink-0" />}
          className="flex-1 min-w-[200px]"
        />
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="small"
            className="flex items-center justify-center"
            onClick={() => navigate('/seller/sales/product-templates')}
          >
            <Download className="h-4 w-4 mr-2 flex-shrink-0" />
            Download Template
          </Button>
          <Button
            variant="outline"
            size="small"
            className="flex items-center justify-center"
            onClick={() => window.open('/upload-guidelines', '_blank')}
          >
            <FileCheck className="h-4 w-4 mr-2 flex-shrink-0" />
            Guidelines
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm text-gray-500 truncate">{stat.label}</p>
                  <div className="flex items-end gap-2">
                    <p className="text-2xl font-bold text-gray-900 truncate">{stat.value}</p>
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                      stat.color === 'green' ? 'bg-green-50 text-green-700' :
                      stat.color === 'blue' ? 'bg-blue-50 text-blue-700' :
                      stat.color === 'purple' ? 'bg-purple-50 text-purple-700' :
                      'bg-orange-50 text-orange-700'
                    }`}>
                      {stat.change}
                    </span>
                  </div>
                </div>
                <div className={`p-2 rounded-lg flex-shrink-0 ${
                  stat.color === 'green' ? 'bg-green-100' :
                  stat.color === 'blue' ? 'bg-blue-100' :
                  stat.color === 'purple' ? 'bg-purple-100' :
                  'bg-orange-100'
                }`}>
                  <Icon className={`h-5 w-5 ${
                    stat.color === 'green' ? 'text-green-600' :
                    stat.color === 'blue' ? 'text-blue-600' :
                    stat.color === 'purple' ? 'text-purple-600' :
                    'text-orange-600'
                  }`} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Upload Area */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-lg flex-shrink-0">
                    <Upload className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">Upload Products</h3>
                    <p className="text-sm text-gray-600 truncate">Drag & drop or select your file</p>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium border flex items-center gap-2 flex-shrink-0 ${getStatusColor(uploadStatus)}`}>
                  {getStatusIcon(uploadStatus)}
                  <span className="capitalize truncate">{uploadStatus || 'Ready'}</span>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className={`
                border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300
                ${uploadStatus === 'uploading' ? 'border-blue-300 bg-blue-50' :
                  uploadStatus === 'processing' ? 'border-purple-300 bg-purple-50' :
                  uploadStatus === 'completed' ? 'border-green-300 bg-green-50' :
                  'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }
              `}>
                {!file ? (
                  <>
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-100 to-cyan-100 flex items-center justify-center flex-shrink-0">
                      <Cloud className="h-10 w-10 text-blue-600 flex-shrink-0" />
                    </div>
                    <p className="text-lg font-medium text-gray-900 mb-2">Upload Product File</p>
                    <p className="text-gray-600 mb-4">Supports CSV, XLSX, XLS (Max 10MB)</p>
                    
                    <div className="flex flex-col sm:flex-row justify-center gap-3 mb-4">
                      <label className="cursor-pointer">
                        <input
                          id="file-upload"
                          type="file"
                          accept=".csv,.xlsx,.xls,.ods"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                        <Button 
                          className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 flex items-center justify-center"
                        >
                          <Upload className="h-4 w-4 mr-2 flex-shrink-0" />
                          Select File
                        </Button>
                      </label>
                      <Button 
                        variant="outline"
                        onClick={() => document.getElementById('file-upload').click()}
                        className="flex items-center justify-center"
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-2 flex-shrink-0" />
                        Browse Files
                      </Button>
                    </div>
                    
                    <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center flex-shrink-0">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-1 flex-shrink-0" />
                        <span>Instant Validation</span>
                      </div>
                      <div className="flex items-center flex-shrink-0">
                        <Shield className="h-4 w-4 text-blue-500 mr-1 flex-shrink-0" />
                        <span>Secure Upload</span>
                      </div>
                      <div className="flex items-center flex-shrink-0">
                        <Zap className="h-4 w-4 text-purple-500 mr-1 flex-shrink-0" />
                        <span>Fast Processing</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                      <div className={`p-4 rounded-xl flex-shrink-0 ${
                        uploadStatus === 'completed' ? 'bg-green-100' :
                        uploadStatus === 'processing' ? 'bg-purple-100' :
                        'bg-blue-100'
                      }`}>
                        <FileSpreadsheet className={`h-12 w-12 flex-shrink-0 ${
                          uploadStatus === 'completed' ? 'text-green-600' :
                          uploadStatus === 'processing' ? 'text-purple-600' :
                          'text-blue-600'
                        }`} />
                      </div>
                      <div className="text-left min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{file.name}</p>
                        <p className="text-sm text-gray-600 truncate">
                          {(file.size / 1024 / 1024).toFixed(2)} MB • {uploadStatus === 'completed' ? 'Processed' : 'Processing'}
                        </p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    {(uploadStatus === 'uploading' || uploadStatus === 'processing') && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700 truncate">
                            {uploadStatus === 'uploading' ? 'Uploading...' : 'Processing...'}
                          </span>
                          <span className="font-medium text-gray-900 flex-shrink-0">{uploadProgress}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all duration-300 ${
                              uploadStatus === 'uploading' 
                                ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
                                : 'bg-gradient-to-r from-purple-500 to-pink-500'
                            }`}
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row justify-center gap-3">
                      {uploadStatus === 'completed' ? (
                        <>
                          <Button
                            variant="outline"
                            onClick={resetUpload}
                            className="min-w-[140px] flex items-center justify-center"
                          >
                            <RefreshCw className="h-4 w-4 mr-2 flex-shrink-0" />
                            Upload Another
                          </Button>
                          <Button
                            onClick={() => navigate('/seller/sales')}
                            className="min-w-[140px] bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 flex items-center justify-center"
                          >
                            <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                            View Products
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            onClick={resetUpload}
                            disabled={uploadStatus === 'uploading'}
                            className="min-w-[140px] flex items-center justify-center"
                          >
                            <XCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                            Cancel
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => document.getElementById('file-upload').click()}
                            disabled={uploadStatus === 'uploading'}
                            className="min-w-[140px] flex items-center justify-center"
                          >
                            <FileSpreadsheet className="h-4 w-4 mr-2 flex-shrink-0" />
                            Change File
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Quick Tips */}
          <Card>
            <div className="p-6">
              <h4 className="font-medium text-gray-900 mb-4">Pro Tips for Successful Uploads</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">Use Templates</p>
                    <p className="text-sm text-gray-600">Download our pre-formatted templates for best results</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                    <Database className="h-5 w-5 text-green-600 flex-shrink-0" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">Batch Processing</p>
                    <p className="text-sm text-gray-600">Upload up to 1000 products in a single file</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                    <Shield className="h-5 w-5 text-purple-600 flex-shrink-0" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">Data Validation</p>
                    <p className="text-sm text-gray-600">Our system validates data in real-time</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                    <Zap className="h-5 w-5 text-orange-600 flex-shrink-0" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">Quick Preview</p>
                    <p className="text-sm text-gray-600">Preview data before final submission</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Template Selection & Info */}
        <div className="space-y-6">
          {/* Template Selection */}
          <Card>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg flex-shrink-0">
                    <Download className="h-5 w-5 text-green-600 flex-shrink-0" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">Templates</h3>
                    <p className="text-sm text-gray-600 truncate">Choose a template format</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className={`
                      p-4 rounded-xl border-2 transition-all cursor-pointer
                      ${selectedTemplate === template.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }
                    `}
                    onClick={() => setSelectedTemplate(template.id)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                          template.color === 'blue' ? 'bg-blue-500' :
                          template.color === 'green' ? 'bg-green-500' :
                          template.color === 'purple' ? 'bg-purple-500' :
                          'bg-orange-500'
                        }`} />
                        <h4 className="font-medium text-gray-900 truncate">{template.name}</h4>
                      </div>
                      {selectedTemplate === template.id && (
                        <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2 truncate">{template.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded flex-shrink-0">
                        {template.fields} fields
                      </span>
                      <Button
                        variant="ghost"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          alert(`Downloading ${template.name}...`);
                        }}
                        className="flex items-center justify-center"
                      >
                        <Download className="h-4 w-4 flex-shrink-0" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button
                fullWidth
                className="mt-6 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 flex items-center justify-center"
                onClick={() => {
                  const template = templates.find(t => t.id === selectedTemplate);
                  alert(`Downloading ${template?.name}...`);
                }}
              >
                <Download className="h-4 w-4 mr-2 flex-shrink-0" />
                Download Selected Template
              </Button>
            </div>
          </Card>

          {/* Recent Uploads */}
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">Recent Uploads</h4>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => alert('View all uploads')}
                  className="flex items-center justify-center"
                >
                  View All
                </Button>
              </div>
              <div className="space-y-3">
                {[
                  { name: 'electronics.csv', date: '2 hours ago', status: 'success', count: 45 },
                  { name: 'clothing_batch.xlsx', date: '1 day ago', status: 'success', count: 120 },
                  { name: 'furniture_import.csv', date: '2 days ago', status: 'warning', count: 67 }
                ].map((upload, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileSpreadsheet className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{upload.name}</p>
                        <p className="text-xs text-gray-500 truncate">{upload.date} • {upload.count} products</p>
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${
                      upload.status === 'success' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {upload.status === 'success' ? 'Completed' : 'Review'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Quick Actions Card */}
          <Card>
            <div className="p-6">
              <h4 className="font-medium text-gray-900 mb-4">Quick Actions</h4>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => navigate('/seller/sales/add-product')}
                  className="flex items-center justify-start"
                >
                  <Plus className="h-4 w-4 mr-2 flex-shrink-0" />
                  Add Single Product
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => navigate('/seller/sales/product-templates')}
                  className="flex items-center justify-start"
                >
                  <FileText className="h-4 w-4 mr-2 flex-shrink-0" />
                  Browse Templates
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => window.open('/pricing-guide', '_blank')}
                  className="flex items-center justify-start"
                >
                  <span className="mr-2 font-medium">₹</span>
                  Pricing Guide (INR)
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BulkUpload;